// ── Kostenart API client — TanStack Query hooks ───────────────────────────────
// Backed by /api/dim/kostenart (dimKostenart.js, dim.Kostenart table, mig 171).
//
// Auth enforced server-side: writes require IT.Admin (only).
// Reads: any authenticated user.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../shared/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Kostenart {
  id: number
  code: string
  name: string
  parentId: number | null
  kind: 'cost' | 'revenue'
  sortOrder: number
  active: boolean
  createdAt: string
}

export interface CreateKostenartPayload {
  name: string
  kind: 'cost' | 'revenue'
  parentId: number | null
}

export interface UpdateKostenartPayload {
  name?: string
  active?: boolean
  sortOrder?: number
}

// ── Query keys ────────────────────────────────────────────────────────────────

const LIST_KEY = ['dim-kostenart'] as const
const USAGE_KEY = (id: number) => ['dim-kostenart-usage', id] as const

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** Fetches all Kostenart rows (no filter — all 16 seeds + any user-created rows). */
export function useKostenarten() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: () => api.get<Kostenart[]>('/api/dim/kostenart'),
    staleTime: 30_000,
  })
}

/**
 * Lazy usage-count query.  Call `refetch()` when the user attempts to deactivate
 * a row; show the count in the confirmation modal before the update mutation fires.
 */
export function useKostenartUsage(id: number) {
  return useQuery({
    queryKey: USAGE_KEY(id),
    queryFn: () => api.get<{ count: number }>(`/api/dim/kostenart/${id}/usage-count`),
    // Disabled by default — caller must refetch() on demand.
    enabled: false,
    staleTime: 0,
  })
}

export function useCreateKostenart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateKostenartPayload) =>
      api.post<Kostenart>('/api/dim/kostenart', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

export function useUpdateKostenart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateKostenartPayload }) =>
      api.patch<Kostenart>(`/api/dim/kostenart/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}
